import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Mail, MessageSquare, HelpCircle, Heart } from "lucide-react";
import MainLayout from "@/layouts/MainLayout";
import ParallaxHero from "@/components/ParallaxHero";
import AnimatedSection from "@/components/AnimatedSection";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import teamPhoto from "@/assets/team-photo.jpg";

const Contact = () => {
  const { settings } = useSiteSettings();
  const contactEmail = settings.social_email || "rogueskullgames@gmail.com";
  const mailtoHref = `mailto:${contactEmail}`;

  const { data: heroSetting } = useQuery({
    queryKey: ["site-setting", "contact_hero_image"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "contact_hero_image")
        .maybeSingle();
      if (error) throw error;
      return data?.setting_value;
    },
  });

  const heroImage = heroSetting || teamPhoto;

  const reasons = [
    { icon: Heart, title: settings.contact_reason_1_title, text: settings.contact_reason_1_text },
    { icon: HelpCircle, title: settings.contact_reason_2_title, text: settings.contact_reason_2_text },
    { icon: MessageSquare, title: settings.contact_reason_3_title, text: settings.contact_reason_3_text },
  ].filter((r) => r.title || r.text);

  return (
    <MainLayout>
      <Helmet>
        <title>Contact | Rogue Skull Games</title>
        <meta
          name="description"
          content="Get in touch with Rogue Skull Games. Say hi, ask a rule question, or send us anything else via email."
        />
        <link rel="canonical" href="https://rogueskullgames.com/contact" />
        <meta property="og:title" content="Contact | Rogue Skull Games" />
        <meta
          property="og:description"
          content="Reach out to Rogue Skull Games with questions, rule clarifications, or just to say hello."
        />
        <meta property="og:url" content="https://rogueskullgames.com/contact" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero */}
      <ParallaxHero
        imageUrl={heroImage}
        alt="Rogue Skull Games contact"
        className="landing-page-hero"
        overlayClassName="hero-overlay-fade"
        heightClass="min-h-[620px] md:min-h-[600px]"
      >
        <div className="container hero-content z-20">
          <div className="">
            <div className="max-w-5xl">
              <span className="inline-block text-blood-red font-semibold tracking-[0.3em] mb-2 animate-slide-in">
                {settings.contact_hero_label}
              </span>
              <h1 className="text-4xl lg:text-6xl text-dark-foreground font-display font-bold leading-[1.1] mb-6 animate-fade-up">
                {settings.contact_hero_title || "Contact Rogue Skull Games"}
              </h1>
              <p
                className="text-dark-foreground/80 text-lg md:text-xl leading-relaxed max-w-2xl animate-fade-up"
                style={{ animationDelay: "0.2s" }}
              >
                {settings.contact_hero_text}
              </p>
            </div>
          </div>
        </div>
      </ParallaxHero>

      {/* Content */}
      <div className="py-20 md:py-24">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-16 mb-16">
              {reasons.map((reason, index) => (
                <AnimatedSection key={reason.title} animation="fade-up" delay={index * 100}>
                  <a href={mailtoHref} className="group block h-full p-4 overflow-hidden transition-all duration-500">
                    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-blood-red/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <reason.icon className="w-8 h-8 text-blood-red mb-5" />
                    <h3 className="text-2xl font-display font-bold text-[hsl(0,0%,10%)] mb-3 uppercase tracking-wide">
                      {reason.title}
                    </h3>
                    <p className="text-[hsl(0,0%,40%)] leading-relaxed">{reason.text}</p>
                  </a>
                </AnimatedSection>
              ))}
            </div>

            <AnimatedSection animation="scale" className="text-center">
              <div className="inline-block border border-[hsl(0,0%,90%)] bg-[#0c0c0c] p-10 md:p-14">
                <Mail className="w-10 h-10 text-blood-red mx-auto mb-6" />
                <h3 className="text-2xl md:text-3xl font-display font-bold text-[hsl(0,0%,10%)] mb-4">
                  {settings.contact_cta_title}
                </h3>
                <p className="text-[hsl(0,0%,40%)] mb-8 max-w-md mx-auto">{settings.contact_cta_text}</p>
                <a href={mailtoHref} className="btn-solid-dark inline-flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  {settings.contact_cta_button_text}
                </a>
                <p className="text-[hsl(0,0%,50%)] text-sm mt-6">{contactEmail}</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Contact;
